import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { FmDocument } from '@api';
import { TranslateService } from '@ngx-translate/core';
import { rxSubscriptionContainerMixin } from '@shared/mixins';
import { FileService } from '@shared/services/file.service';
import { MandantConfigService } from '@strategies/mandant-config.service';
import { ToastrService } from 'ngx-toastr';
import { IDetailFields } from './models/detail-fields.model';

@Component({
  selector: 'app-detail-fields-base',
  templateUrl: './detail-fields.base.html',
  styleUrls: ['./detail-fields.base.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DetailFieldsBaseComponent extends rxSubscriptionContainerMixin() implements OnInit {
  private fileService = inject(FileService);
  private translateService = inject(TranslateService);
  private toastr = inject(ToastrService);
  private cd = inject(ChangeDetectorRef);
  private mandantConfigService = inject(MandantConfigService);

  @Input() allowFileUpload = true;

  @Input() detailFields: Array<IDetailFields> | undefined;

  @Input() data: any;

  @Input() readonly = false;

  temp1: string;

  temp2: number;

  fileIsLoading = '';

  fileDownloadState: boolean[] = [];

  documents: any[] = [];

  mandant: string;

  getMetadata(field) {
    return field.fileMetadata;
  }

  ngOnInit(): void {
    const documentField = this.detailFields?.find(
      f => f.type === 'files' || f.type === 'typed-files'
    );
    this.documents = this.getFiles(documentField?.key);

    if (this.detailFields) {
      const hasAllignedFields: boolean =
        this.detailFields?.filter(f => f.type === 'allignedField')?.length > 0;
      if (hasAllignedFields === true) {
        this.temp1 = 'column';
        this.temp2 = 0;
      } else {
        this.temp1 = 'row wrap';
        this.temp2 = 1;
      }
    }
    this.mandant = this.mandantConfigService.mandant.name;
  }

  getLabel(field: IDetailFields) {
    try {
      let { label } = field;

      if (field.transformLabelFn) {
        const value = field?.key?.split('.').reduce((o, i) => o[i], this.data);
        label = field.transformLabelFn(value);
      }

      return !(label === null || label === undefined) ? label : '--';
    } catch (_err) {
      return '--';
    }
  }

  getData(field: IDetailFields) {
    try {
      if (field.key === undefined || field.key === null || !this.data) {
        return '--';
      }
      let val = field.key.split('.').reduce((o, i) => o[i], this.data);

      if (field.transformValueFn) {
        val = field.transformValueFn(val);
      }

      return !(val === null || val === undefined) ? val : '--';
    } catch (_err) {
      return '--';
    }
  }

  getLink(field: IDetailFields) {
    if (this.data && field.hrefParamKey) {
      return `${field.href}${this.data[field.hrefParamKey]}` || '#';
    }
    return '';
  }

  getTableData(key: string | undefined) {
    if (key && this.data && this.data[key]) {
      return this.data[key];
    }
    return [];
  }

  private getFiles(key: string | undefined) {
    if (!key || !this.data) {
      return [];
    }
    const fileArray = key.split('.').reduce((o, i) => o[i], this.data) || [];
    this.fileDownloadState = new Array(fileArray.length).fill(false);
    return fileArray;
  }

  visible(field: IDetailFields) {
    if (field.hiddenIfEmtpy) {
      return this.getData(field) !== '--';
    }
    return true;
  }

  async downloadFile(file: FmDocument, index: number) {
    if (!file.blobFilename) {
      console.warn('cannot load file without a blobFilename');
      this.toastr.error(this.translateService.instant('detailFields.fileDownload.warn'));
      return;
    }
    this.fileDownloadState[index] = true;

    this.cd.detectChanges();

    this.pushSubscription(
      this.fileService.downloadFile(file.blobFilename, file.contentType).subscribe(
        res => {
          const fileResponse = res;
          this.fileDownloadState[index] = false;
          this.cd.detectChanges();

          if (fileResponse !== null) {
            this.fileService.saveFile(fileResponse, file.name);
          }
        },
        err => {
          console.warn(err);
          this.toastr.error(this.translateService.instant('detailFields.fileDownload.warn'));
          this.fileDownloadState[index] = false;
          this.cd.detectChanges();
        }
      )
    );
  }

  fileRead(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => {
        resolve(fr.result as string);
      };
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });
  }
}
